import { Component, ElementRef, HostBinding, Input, OnInit, ViewChild, signal, computed, input } from '@angular/core';
import { first, firstValueFrom, Subscription } from 'rxjs';
import {
	Activity,
	AuthService as MtLibsAuthService,
	CognitoUser,
	Content,
	ContentScoringMethod,
	TokenPack,
	ReportingService as MtLibsReportingService,
	RubricService as MtLibsRubricService,
	EngineService as MtLibsEngineService,
	UuidService as MtLibsUuidService,
	ClientService as MtLibsClientService,
	RubricLevelsAndSkillDescriptionsPayload,
	RubricLevel,
	Rubric,
	ScoreTakePayload,
	ScoreTakeWithAssessmentPayload,
	StrideUtils,
	EngineRubricSkill,
	Course,
	StrideAssessmentPayload,
	_,
	Product,
} from '@mira-avant/mtlibs';
import { AuthService } from '../../../../../services/auth.service';
import { ReportingService } from '../../../../../services/reporting.service';
import { TakeResponse, TakeService } from '../../../../../services/take.service';
import { ActivatedRoute } from '@angular/router';
import { RubricService } from '../../../../../services/rubric.service';
import { AssessmentService } from '../../../../../services/assessment.service';
import { CourseService } from '../../../../../services/course.service';
import { SafePipe } from '../../../../../pipes/safe.pipe';
import { ClientService } from '../../../../../services/client.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { EngineService } from '../../../../../services/engine.service';
import { UrlHelper } from '../../../../../utils/url.helper';
import { CommonModule } from '@angular/common';

@Component({
	selector: 'app-hubs-player',
	standalone: true,
	imports: [CommonModule, SafePipe, NzSpinModule],
	templateUrl: './hubs-player.component.html',
	styleUrl: './hubs-player.component.scss',
})
export class HubsPlayerComponent implements OnInit {
	@HostBinding('class.non-featured')
	get isNonFeatured() {
		return !this.content?.featured_mode;
	}
	@ViewChild('iframe') private iframe!: ElementRef<HTMLIFrameElement>;
	@Input('activity') public activity: Activity | undefined;
	@Input('content') public content: Content | undefined;

	dontTrackTake = input<boolean>(false);
	loading = signal<boolean>(true);
	userId = signal<string | undefined>(undefined);
	takeNumber = signal<number | undefined>(undefined);
	course = signal<Course | undefined>(undefined);
	newTake = signal<TakeResponse | undefined>(undefined);
	rubric = signal<Rubric | undefined>(undefined);
	token = signal<string | undefined>(undefined);

	private mtLibsAuth: MtLibsAuthService = new MtLibsAuthService();
	private mtLibsReporting: MtLibsReportingService = new MtLibsReportingService();
	private mtLibsRubricService: MtLibsRubricService = new MtLibsRubricService();
	private mtLibsEngineService: MtLibsEngineService = new MtLibsEngineService();
	private mtLibsUuidService: MtLibsUuidService = new MtLibsUuidService();
	private mtLibsClientService: MtLibsClientService = new MtLibsClientService();
	private sub: Subscription = new Subscription();
	private handle: ReturnType<typeof setInterval> | undefined;

	public safeUrl = computed(() => this.url());

	constructor(
		public route: ActivatedRoute,
		private authService: AuthService,
		public reportingService: ReportingService,
		public takeService: TakeService,
		private rubricService: RubricService,
		private assessmentService: AssessmentService,
		private engineService: EngineService,
		private courseService: CourseService,
		private clientService: ClientService,
		private zorroMessenger: NzMessageService
	) {
		this.authService.user$.pipe(first()).subscribe((user: CognitoUser | null) => {
			this.userId.set(user?.uuid);
		});

		this.sub.add(
			this.authService.tokenPack$.subscribe((tokenPack: TokenPack | null) => {
				this.token.set(tokenPack ? tokenPack.token : undefined);
			})
		);
	}

	async ngOnInit(): Promise<void> {
		this.registerPostRobotListeners();

		if (!this.activity || !this.content) {
			throw new Error('Missing activity or content');
		}

		//Load the rubric if this content is associated with a rubric level.
		if (this.content.rubric_level_uuid) {
			this.rubric.set(await this.rubricService.getRubricByLevelUuid$(this.content.rubric_level_uuid));

			//Load the rubric levels for the rubric as well.
			await this.rubricService.getRubricLevels$(this.rubric()!.code);
		}

		if (this.dontTrackTake()) {
			this.loading.set(false);
			this.course.set(await this.courseService.get$(this.route.snapshot.params['courseUuid']));
			return;
		} else {
			await this.takeService
				.createTake$({
					course_uuid: this.route.snapshot.params['courseUuid'],
					location_uuid: this.route.snapshot.params['locationUuid'],
					section_uuid: this.route.snapshot.params['sectionUuid'],
					unit_uuid: this.route.snapshot.params['unitUuid'],
					lesson_uuid: this.route.snapshot.params['lessonUuid'],
					activity_uuid: this.activity.uuid,
					content_uuid: this.content.uuid,
					scoring_method: this.content.scoring_method as ContentScoringMethod,
				})
				.then(async takeResponse => {
					if (!this.activity || !this.content) {
						throw new Error('Missing activity or content');
					}

					this.loading.set(false);
					this.takeNumber.set(takeResponse.take.take);
					this.newTake.set(takeResponse);

					if ([ContentScoringMethod.VIEWED].includes(this.content.scoring_method as ContentScoringMethod)) {
						await this.takeService.completeTake$({
							course_uuid: this.route.snapshot.params['courseUuid'],
							location_uuid: this.route.snapshot.params['locationUuid'],
							section_uuid: this.route.snapshot.params['sectionUuid'],
							unit_uuid: this.route.snapshot.params['unitUuid'],
							lesson_uuid: this.route.snapshot.params['lessonUuid'],
							activity_uuid: this.activity.uuid,
							content_uuid: this.content.uuid,
							take: this.newTake()!.take.take,
							duration: 0,
						});
					} else {
						console.warn('Skipping reporting & XAPI events as this content is not scored.');
					}

					this.course.set(await this.courseService.get$(this.route.snapshot.params['courseUuid']));

					return takeResponse;
				})
				.catch(error => {
					this.zorroMessenger.error('Unable to begin activity. Please try again later.');
				});
		}

		// setTimeout(async () => {
		// 	await this.debugScoreTakeWithAssessment();
		// }, 10000);
	}

	private async debugScoreTakeWithAssessment(): Promise<void> {
		if (this.dontTrackTake()) {
			console.warn('Skipping scoreTakeWithAssessment because dontTrackTake is true');
			return;
		}

		if (!this.activity || !this.content || !this.newTake()) {
			throw new Error('Missing activity or content');
		}

		if (!this.userId()) {
			throw new Error('Missing userId');
		}

		const payload: ScoreTakeWithAssessmentPayload = {
			command: 'scoreTakeWithAssessment',
			min: 0,
			score: 35,
			max: 100,
			assessment: {
				rubricLevel: 3,
				strengths: [
					{
						title: 'Strengths',
						points: [
							{
								description: 'Demonstrates understanding of key concepts.',
								subPoints: [
									{ description: 'Can explain concepts clearly.' },
									{ description: 'Applies concepts in practical scenarios.' },
								],
							},
						],
					},
				],
				needsImprovement: [
					{
						title: 'Needs Improvement',
						points: [
							{
								description: 'Struggles with advanced topics.',
								subPoints: [
									{ description: 'Has difficulty with complex problem-solving.' },
									{ description: 'Needs more practice with applications.' },
								],
							},
						],
					},
				],
				strideForward: [
					{
						title: 'Stride Forward',
						points: [
							{
								description: 'Focus on advanced topics and applications.',
								subPoints: [
									{ description: 'Engage in more complex exercises.' },
									{ description: 'Seek additional resources for learning.' },
								],
							},
						],
					},
				],
			},
		};

		await this.takeService.completeTake$({
			course_uuid: this.route.snapshot.params['courseUuid'],
			location_uuid: this.route.snapshot.params['locationUuid'],
			section_uuid: this.route.snapshot.params['sectionUuid'],
			unit_uuid: this.route.snapshot.params['unitUuid'],
			lesson_uuid: this.route.snapshot.params['lessonUuid'],
			activity_uuid: this.activity.uuid,
			content_uuid: this.content.uuid,
			take: this.newTake()!.take.take,
			duration: 0,
			questions_correct: payload.score,
			questions_total: payload.max,
		});

		const assessmentPayload: Omit<StrideAssessmentPayload, 'client_uuid' | 'student_cognito_uuid'> = {
			course_uuid: this.route.snapshot.params['courseUuid'],
			location_uuid: this.route.snapshot.params['locationUuid'],
			section_uuid: this.route.snapshot.params['sectionUuid'],
			unit_uuid: this.route.snapshot.params['unitUuid'],
			lesson_uuid: this.route.snapshot.params['lessonUuid'],
			activity_uuid: this.activity.uuid,
			content_uuid: this.content.uuid,
			take: this.newTake()!.take.take,
			skill_code: this.content?.skill_code || '',
			rubric_code: this.rubric()?.code || '',
			rubric_level_uuid: this.content.rubric_level_uuid || '',
			rubric_level_level: payload.assessment.rubricLevel,
			strengths: payload.assessment.strengths,
			needsImprovement: payload.assessment.needsImprovement,
			strideForward: payload.assessment.strideForward,
		};

		await this.assessmentService.createStrideAssessment$(assessmentPayload);
	}

	private waitForIframeWindow(): Promise<void> {
		return new Promise<void>(resolve => {
			let interval = setInterval(() => {
				if (this.iframe?.nativeElement?.contentWindow) {
					clearInterval(interval);
					resolve();
				}
			}, 100);
		});
	}

	private registerPostRobotListeners(): void {
		this.mtLibsAuth.listenForGetTokenCode(() => {
			return this.authService.createTokenPackCode();
		});

		this.mtLibsClientService.listenForGetClientSubdomain(async () => {
			if (this.dontTrackTake()) {
				return UrlHelper.getLeftMostSubDomains();
			}

			const subdomain: string = await firstValueFrom(this.clientService.selectedClient$).then(
				client => client?.subdomain || '...missing...'
			);
			return subdomain;
		});

		this.mtLibsUuidService.listenForGetUuids(async () => {
			if (!this.activity || !this.content) {
				throw new Error('Missing activity or content');
			}

			const clientUuid: string =
				(await this.clientService.selectedClientBs$.value?.uuid) || this.route.snapshot.params['clientUuid'];

			if (this.dontTrackTake()) {
				console.warn('Returning -1 for take as dontTrackTake is true.');
			}

			//Either dontTrackTake must be true or newTake must be defined, otherwise we don't have a take number to return which will cause issues in the Hubs player.
			if (!this.dontTrackTake() && !this.newTake()) {
				throw new Error(
					'newTake must be defined if dontTrackTake is false because we need a take number to return to the Hubs player.'
				);
			}

			const takeNumber = this.dontTrackTake() ? -1 : this.newTake()!.take.take;

			const uuidPack = {
				client_uuid: clientUuid,
				course_uuid: this.route.snapshot.params['courseUuid'],
				location_uuid: this.route.snapshot.params['locationUuid'],
				section_uuid: this.route.snapshot.params['sectionUuid'],
				unit_uuid: this.route.snapshot.params['unitUuid'],
				lesson_uuid: this.route.snapshot.params['lessonUuid'],
				activity_uuid: this.activity.uuid,
				content_uuid: this.content.uuid,
				take: takeNumber,
				rubric_level_uuid: this.content.rubric_level_uuid,
			};

			return uuidPack;
		});

		this.mtLibsReporting.listenForCompleteTake(async (payload: unknown) => {
			if (this.dontTrackTake()) {
				console.warn('Skipping completeTake because dontTrackTake is true');
				return;
			}

			if (!this.activity || !this.content || !this.newTake()) {
				throw new Error('Missing activity or content');
			}
			console.info('Angular received completeTake', this.takeNumber(), payload);

			if ([ContentScoringMethod.PASS_FAIL].includes(this.content.scoring_method as ContentScoringMethod)) {
				await this.takeService.completeTake$({
					course_uuid: this.route.snapshot.params['courseUuid'],
					location_uuid: this.route.snapshot.params['locationUuid'],
					section_uuid: this.route.snapshot.params['sectionUuid'],
					unit_uuid: this.route.snapshot.params['unitUuid'],
					lesson_uuid: this.route.snapshot.params['lessonUuid'],
					activity_uuid: this.activity.uuid,
					content_uuid: this.content.uuid,
					take: this.newTake()!.take.take,
					duration: 0,
				});
			} else {
				throw new Error('COACH attempting to complete a non-pass-fail take. This is not allowed.');
			}
		});

		this.mtLibsReporting.listenForScoreTake(async (payload: ScoreTakePayload) => {
			if (this.dontTrackTake()) {
				console.warn('Angular received scoreTake but skipping because dontTrackTake is true');
				return;
			}

			if (!this.activity || !this.content || !this.newTake()) {
				throw new Error('Missing activity or content');
			}

			if ([ContentScoringMethod.PERCENTAGE].includes(this.content.scoring_method as ContentScoringMethod)) {
				await this.takeService.completeTake$({
					course_uuid: this.route.snapshot.params['courseUuid'],
					location_uuid: this.route.snapshot.params['locationUuid'],
					section_uuid: this.route.snapshot.params['sectionUuid'],
					unit_uuid: this.route.snapshot.params['unitUuid'],
					lesson_uuid: this.route.snapshot.params['lessonUuid'],
					activity_uuid: this.activity.uuid,
					content_uuid: this.content.uuid,
					take: this.newTake()!.take.take,
					duration: 0,
					questions_correct: payload.score,
					questions_total: payload.max,
				});
			} else {
				throw new Error('COACH attempting to score a non-percentage take. This is not allowed.');
			}
		});

		this.mtLibsReporting.listenForScoreTakeWithAssessment(async (payload: ScoreTakeWithAssessmentPayload) => {
			if (this.dontTrackTake()) {
				console.warn('Angular received scoreTakeWithAssessment but skipping because dontTrackTake is true');
				return;
			}

			if (!this.activity || !this.content || !this.newTake()) {
				throw new Error('Missing activity or content');
			}

			if ([ContentScoringMethod.PERCENTAGE].includes(this.content.scoring_method as ContentScoringMethod)) {
				if (!this.userId()) {
					throw new Error('Missing userId');
				}

				await this.takeService.completeTake$({
					course_uuid: this.route.snapshot.params['courseUuid'],
					location_uuid: this.route.snapshot.params['locationUuid'],
					section_uuid: this.route.snapshot.params['sectionUuid'],
					unit_uuid: this.route.snapshot.params['unitUuid'],
					lesson_uuid: this.route.snapshot.params['lessonUuid'],
					activity_uuid: this.activity.uuid,
					content_uuid: this.content.uuid,
					take: this.newTake()!.take.take,
					duration: 0,
					questions_correct: payload.score,
					questions_total: payload.max,
				});

				// Validate the assessment payload
				StrideUtils.validateStrideAssessment(payload.assessment);

				const assessmentPayload: Omit<StrideAssessmentPayload, 'client_uuid' | 'student_cognito_uuid'> = {
					course_uuid: this.route.snapshot.params['courseUuid'],
					location_uuid: this.route.snapshot.params['locationUuid'],
					section_uuid: this.route.snapshot.params['sectionUuid'],
					unit_uuid: this.route.snapshot.params['unitUuid'],
					lesson_uuid: this.route.snapshot.params['lessonUuid'],
					activity_uuid: this.activity.uuid,
					content_uuid: this.content.uuid,
					take: this.newTake()!.take.take,
					skill_code: this.content?.skill_code || '',
					rubric_code: this.rubric()?.code || '',
					rubric_level_uuid: this.content.rubric_level_uuid || '',
					rubric_level_level: payload.assessment.rubricLevel,
					strengths: payload.assessment.strengths,
					needsImprovement: payload.assessment.needsImprovement,
					strideForward: payload.assessment.strideForward,
				};

				await this.assessmentService.createStrideAssessment$(assessmentPayload);
			} else {
				throw new Error(
					'COACH attempting to score a non-percentage take with an assessment. This is not allowed.'
				);
			}
		});

		this.mtLibsRubricService.listenForGetRubricLevels(async () => {
			const rubricLevels: RubricLevel[] = await firstValueFrom(this.rubricService.rubricLevels$);

			const payload: RubricLevelsAndSkillDescriptionsPayload = {
				levels: rubricLevels.map((level: RubricLevel) => {
					return {
						level: {
							...level,
							rubric_level_skill: level.rubric_level_skill?.filter(
								rubricSkill => rubricSkill.skill_code === this.content?.skill_code
							),
						},
						skillDescriptions:
							level.rubric_level_skill && level.rubric_level_skill.length > 0
								? level.rubric_level_skill
										.map(rsl => {
											return {
												skill_code: rsl.skill_code,
												long_description: rsl.long_description,
												prompt_description: rsl.prompt_description,
											};
										})
										.filter(
											skillDescriptor => skillDescriptor.skill_code === this.content?.skill_code
										)
								: [],
					};
				}),
			};

			return payload;
		});

		this.mtLibsEngineService.listenForGetSkillAssessments(async () => {
			if (!this.course() || !this.rubric()) {
				throw new Error('Missing course or rubric');
			}

			const courseProduct: Product = await this.courseService.getProduct$(this.course()!.uuid);

			const skillAssessments: EngineRubricSkill[] = await this.engineService.getEngineRubricSkills$(
				courseProduct.engine_name as string | undefined,
				this.rubric()!.code
			);

			return skillAssessments.filter(prs => prs.skill_code === this.content?.skill_code);
		});
	}

	async ngAfterViewInit() {
		await this.waitForIframeWindow();
		this.authService.setIncludeTokenPackCodeOnRefresh(true);

		//keyboard and mouse events aren't triggering properly in the iframe so run an x-minute timer while
		//in the coach app to keep the token fresh.
		this.handle = setInterval(
			() => this.authService.refreshTokenIfNeeded$(),
			60 * 1000 //once a minute
		);

		this.sub.add(
			this.authService.tokenRefreshCode$.subscribe(code => {
				const targetWindow = this.iframe.nativeElement.contentWindow;

				if (!targetWindow) {
					console.warn('Failed to push refreshed token code to iframe: missing contentWindow');
					return;
				}

				this.mtLibsAuth.pushRefreshTokenCode(code, targetWindow).catch(error => {
					console.warn('Failed to push refreshed token code to iframe', error);
				});
			})
		);
	}

	private async testCompleteTake() {
		if (this.dontTrackTake()) {
			console.warn('Skipping testCompleteTake because dontTrackTake is true');
			return;
		}

		if (!this.activity || !this.content || !this.newTake()) {
			throw new Error('Missing activity or content');
		}

		if ([ContentScoringMethod.PERCENTAGE].includes(this.content.scoring_method as ContentScoringMethod)) {
			await this.takeService.completeTake$({
				course_uuid: this.route.snapshot.params['courseUuid'],
				location_uuid: this.route.snapshot.params['locationUuid'],
				section_uuid: this.route.snapshot.params['sectionUuid'],
				unit_uuid: this.route.snapshot.params['unitUuid'],
				lesson_uuid: this.route.snapshot.params['lessonUuid'],
				activity_uuid: this.activity.uuid,
				content_uuid: this.content.uuid,
				take: this.newTake()!.take.take,
				duration: 0,
				questions_correct: 85,
				questions_total: 100,
			});
		} else {
			throw new Error('COACH attempting to score a non-percentage take. This is not allowed.');
		}
	}

	/**
	 * Get the URL with any name-value pair query string params applied.
	 * @date 11/1/2022 - 3:37:03 PM
	 *
	 * @public
	 * @returns {string}
	 */
	public url(): string {
		// return 'http://localhost:3001/';
		//Get the current url
		const url: string | null | undefined = this.content?.active_text?.content_uri;
		// const url: string =
		// 	'https://dfvv6cb6qiui0.cloudfront.net/?activityId=d956d685-220b-4f93-b9cc-3ff61f30e190';
		// const url: string =
		// 	'https://metabi-poc-hub.com/8FmGm86/?activityId=d956d685-220b-4f93-b9cc-3ff61f30e190';

		if (!url) {
			throw new Error('No URL found for this content.');
		}

		//Get the current query string params, if any
		const queryString: unknown = _.get(this.content, 'active_content_text.data_json.queryString', null);

		// Convert known query-string payload shapes to name-value pairs.
		let queryStringPairs: string[] = [];

		if (queryString instanceof Map) {
			queryStringPairs = Array.from(queryString.entries()).map(([key, value]) => {
				return `${encodeURIComponent(String(key))}=${encodeURIComponent(String(value))}`;
			});
		} else if (Array.isArray(queryString)) {
			queryStringPairs = queryString
				.filter(
					(entry): entry is [string, string] =>
						Array.isArray(entry) &&
						entry.length === 2 &&
						typeof entry[0] === 'string' &&
						typeof entry[1] === 'string'
				)
				.map(([key, value]) => {
					return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
				});
		} else if (queryString && typeof queryString === 'object') {
			queryStringPairs = Object.entries(queryString as Record<string, unknown>).map(([key, value]) => {
				return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
			});
		}

		//Add userId=this.user.id to the query string - temporary solution
		//to pass userId to hubs until we can figure out the windows issue.
		if (this.userId()) {
			queryStringPairs.push(`userId=${this.userId()}`);
		}

		if (this.content) {
			queryStringPairs.push(`contentUuid=${this.content.uuid}`);
		}

		if (this.takeNumber()) {
			queryStringPairs.push(`takeNumber=${this.takeNumber()}`);
		}

		const hasQuestionMark: boolean = url.indexOf('?') > -1;
		const hasQueryStringParams: boolean = queryStringPairs?.length > 0;

		if (hasQueryStringParams) {
			if (hasQuestionMark) {
				return `${url}&${queryStringPairs.join('&')}`;
			} else {
				return `${url}?${queryStringPairs.join('&')}`;
			}
		} else {
			return url;
		}
	}

	ngOnDestroy() {
		this.authService.setIncludeTokenPackCodeOnRefresh(false);
		this.sub?.unsubscribe();
		this.mtLibsAuth.destroy();
		this.mtLibsReporting.destroy();
		this.mtLibsRubricService.destroy();
		this.mtLibsClientService.destroy();
		this.mtLibsEngineService.destroy();
		this.mtLibsUuidService.destroy();

		if (this.handle) {
			clearInterval(this.handle);
			this.handle = undefined;
		}
	}
}
